<template>
  <div class="ai-assistant-panel">
    <!-- 头部 -->
    <div class="assistant-header">
      <h4><i class="el-icon-magic-stick"></i> AI 助手</h4>
      <el-tag v-if="contextLabel" size="mini" type="warning">{{ contextLabel }}</el-tag>
    </div>

    <!-- 快捷操作 -->
    <div v-if="messages.length === 0" class="quick-actions">
      <p class="quick-title">快捷操作</p>
      <div v-for="(action, idx) in quickActions" :key="idx" class="quick-action-btn" @click="sendQuickAction(action)">
        <i :class="action.icon"></i>
        <span>{{ action.label }}</span>
      </div>
    </div>

    <!-- 消息列表 -->
    <div ref="messageList" class="message-list" v-if="messages.length > 0">
      <div v-for="(msg, idx) in messages" :key="idx" :class="['message', msg.role === 'assistant' ? 'message-ai' : 'message-user']">
        <div class="message-avatar">
          <i :class="msg.role === 'assistant' ? 'el-icon-sunny' : 'el-icon-user-solid'"></i>
        </div>
        <div class="message-content">
          <div class="message-text" v-html="renderMarkdown(msg.content)"></div>
          <div v-if="msg.actions" class="message-actions">
            <el-button v-for="(act, aidx) in msg.actions" :key="aidx" size="mini" :type="act.type || 'primary'" @click="act.handler">
              {{ act.label }}
            </el-button>
          </div>
        </div>
      </div>
      <div v-if="thinking" class="message message-ai">
        <div class="message-avatar">
          <i class="el-icon-loading"></i>
        </div>
        <div class="message-content">
          <div class="message-text thinking-text">思考中...</div>
        </div>
      </div>
    </div>

    <!-- 输入区 -->
    <div class="input-area">
      <el-input
        v-model="userInput"
        type="textarea"
        :rows="2"
        placeholder="输入问题，AI 助手为您解答..."
        size="small"
        @keydown.enter.native.prevent="sendMessage"
        :disabled="thinking"
      />
      <div class="input-actions">
        <span class="input-hint">Enter 发送</span>
        <el-button type="primary" size="mini" @click="sendMessage" :disabled="!userInput.trim() || thinking" :loading="thinking">
          <i class="el-icon-s-promotion"></i> 发送
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { callAI } from "@/utils/aiProjectAnalyzer";

export default {
  name: "AIAssistantPanel",
  props: {
    projectContext: {
      type: Object,
      default: () => ({})
    },
    currentStep: {
      type: Number,
      default: 0
    },
    projectTypeLabel: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      messages: [],
      userInput: '',
      thinking: false,
    }
  },
  computed: {
    contextLabel() {
      if (this.projectTypeLabel) return this.projectTypeLabel;
      return this.projectContext.projectName ? '项目中' : '就绪';
    },
    quickActions() {
      const actions = [];
      if (this.currentStep === 1) {
        actions.push({
          icon: 'el-icon-setting',
          label: '如何配置脚手架？',
          handler: () => this.sendMessage('如何配置这个脚手架的各项参数？能否给些建议？'),
        });
      }
      if (this.currentStep === 3) {
        actions.push({
          icon: 'el-icon-discover',
          label: '项目模块建议',
          handler: () => this.sendMessage('根据我的项目结构，有什么模块划分建议？'),
        });
      }
      if (this.currentStep === 5) {
        actions.push({
          icon: 'el-icon-document-copy',
          label: '源码扫描说明',
          handler: () => this.sendMessage('源码扫描有什么作用？如何更好的分类我的代码？'),
        });
      }
      actions.push({
        icon: 'el-icon-question',
        label: '平台使用帮助',
        handler: () => this.sendMessage('请介绍一下这个平台的主要功能和操作流程'),
      });
      if (this.projectContext.projectName) {
        actions.push({
          icon: 'el-icon-s-promotion',
          label: '生成代码建议',
          handler: () => this.sendMessage(`项目「${this.projectContext.projectName}」适合生成什么类型的代码？有什么建议？`),
        });
      }
      return actions;
    }
  },
  methods: {
    async sendMessage(content) {
      const text = content || this.userInput;
      if (!text.trim() || this.thinking) return;

      this.messages.push({ role: 'user', content: text.trim() });
      this.userInput = '';
      this.thinking = true;

      try {
        const contextPrompt = this.buildContextPrompt();
        const fullPrompt = `${contextPrompt}\n\n用户问题: ${text.trim()}\n\n请用中文回答，简洁明了。如果需要给出代码示例，请使用 markdown 代码块。`;

        const result = await callAI(fullPrompt, this.$ai);
        const reply = result.projectDescription
          || result.description
          || result.text
          || (typeof result === 'string' ? result : JSON.stringify(result));

        const msg = { role: 'assistant', content: reply };

        // 根据上下文添加操作按钮
        const actions = this.getContextActions(text.trim());
        if (actions.length > 0) msg.actions = actions;

        this.messages.push(msg);
      } catch (e) {
        console.error('AI 助手响应失败:', e);
        this.messages.push({
          role: 'assistant',
          content: '抱歉，AI 助手暂时无法响应，请稍后重试。错误: ' + (e.message || '未知错误')
        });
      } finally {
        this.thinking = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    sendQuickAction(action) {
      if (typeof action.handler === 'function') {
        action.handler();
      }
    },
    buildContextPrompt() {
      const ctx = this.projectContext;
      return `你是一个项目开发助手，帮助用户使用代码生成平台。当前上下文：
- 当前步骤: ${this.getStepLabel(this.currentStep)}
- 项目名称: ${ctx.projectName || '未选择'}
- 项目类型: ${this.projectTypeLabel || '未知'}
- 模块数量: ${ctx.moduleCount || 0}
- 已扫描文件: ${ctx.scannedFileCount || 0}

请根据用户的问题提供帮助。`;
    },
    getStepLabel(step) {
      const labels = ['选择或创建项目', '配置脚手架', '选择本地路径', '项目模块分析', '确认录入模块', '源码扫描标注'];
      return labels[step] || '未知步骤';
    },
    getContextActions(query) {
      const actions = [];
      const q = query.toLowerCase();

      if (q.includes('代码') || q.includes('生成')) {
        actions.push({
          label: '去生成代码',
          type: 'success',
          handler: () => this.$emit('action', 'generateCode'),
        });
      }
      if (q.includes('脚手架') || q.includes('配置')) {
        actions.push({
          label: '配置脚手架',
          type: 'primary',
          handler: () => this.$emit('action', 'configureScaffold'),
        });
      }
      if (q.includes('扫描') || q.includes('分类')) {
        actions.push({
          label: '开始扫描',
          type: 'warning',
          handler: () => this.$emit('action', 'startScan'),
        });
      }

      return actions;
    },
    renderMarkdown(text) {
      if (!text) return '';
      // Simple markdown rendering — convert code blocks and inline code
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      // Code blocks
      html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>');
      // Inline code
      html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
      // Bold
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // Lists
      html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
      // Newlines
      html = html.replace(/\n/g, '<br>');
      return html;
    },
    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },
    clearMessages() {
      this.messages = [];
    }
  }
}
</script>

<style scoped>
.ai-assistant-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
}

.assistant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.assistant-header h4 {
  margin: 0;
  font-size: 14px;
}

.quick-actions {
  padding: 12px 16px;
}

.quick-title {
  font-size: 12px;
  color: #909399;
  margin: 0 0 8px;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  transition: all 0.2s;
}

.quick-action-btn:hover {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409EFF;
}

.message-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}

.message {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.message-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}

.message-ai .message-avatar {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
}

.message-user .message-avatar {
  background: #ecf5ff;
  color: #409EFF;
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-text {
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
  background: #f5f7fa;
  padding: 8px 12px;
  border-radius: 8px;
  word-break: break-word;
}

.message-user .message-text {
  background: #ecf5ff;
  color: #303133;
}

.thinking-text {
  color: #909399;
  font-style: italic;
}

.message-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.input-area {
  padding: 12px 16px;
  border-top: 1px solid #ebeef5;
}

.input-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.input-hint {
  font-size: 11px;
  color: #c0c4cc;
}

:deep(pre) {
  background: #f0f2f5;
  padding: 8px 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  margin: 8px 0;
}

:deep(code) {
  background: #f0f2f5;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}

:deep(li) {
  margin-bottom: 4px;
  list-style: disc;
  margin-left: 16px;
}
</style>
